import Swiper, { Grid, Navigation } from 'swiper';
import { swiperOnResize } from '../../utils/swiperOnResize.utils.js';

const heightControllers = new WeakMap();

const createHeightController = (swiperEl) => {
  let resizeTimeout = null;
  let resizeFrame = null;

  const setStableHeight = () => {
    const swiper = swiperEl.swiper;
    const wrapperEl = swiperEl.querySelector('.swiper-wrapper');
    const slides = Array.from(swiperEl.querySelectorAll('.swiper-slide'));
    const items = Array.from(swiperEl.querySelectorAll('.system-sec__item'));

    if (swiper?.destroyed || !swiper || !wrapperEl || !slides.length || !items.length) {
      return;
    }

    swiperEl.style.height = 'auto';
    wrapperEl.style.height = 'auto';
    slides.forEach((slide) => {
      slide.style.height = 'auto';
    });

    const maxItemHeight = Math.max(...items.map((item) => item.offsetHeight));
    const gap = Number.parseFloat(swiper.params.spaceBetween) || 0;
    const rows = swiper.params.grid.rows;
    const slideHeight = Math.ceil(maxItemHeight + 8 / rows);
    const height = slideHeight * rows + gap * (rows - 1);

    swiperEl.style.removeProperty('height');
    wrapperEl.style.removeProperty('height');
    slides.forEach((slide) => {
      slide.style.removeProperty('height');
    });
    swiperEl.style.setProperty('--system-swiper-height', `${height}px`);
    swiperEl.style.setProperty('--system-swiper-slide-height', `${slideHeight}px`);
    swiper.update();
  };

  const requestStableHeight = () => {
    if (resizeFrame) {
      cancelAnimationFrame(resizeFrame);
    }

    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = null;
      setStableHeight();
    });
  };

  const onResize = () => {
    if (resizeTimeout) {
      return;
    }

    resizeTimeout = setTimeout(() => {
      resizeTimeout = null;
      requestStableHeight();
    }, 150);
  };

  requestStableHeight();
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('orientationchange', onResize, { passive: true });

  if (document.fonts?.ready) {
    document.fonts.ready.then(requestStableHeight).catch(requestStableHeight);
  } else {
    window.addEventListener('load', requestStableHeight, { once: true });
  }

  return { requestStableHeight };
};

const createSwiperSettings = (rows) => ({
  modules: [Grid, Navigation],
  observer: true,
  observeParents: true,
  slidesPerView: 1,
  slidesPerGroup: 1,
  spaceBetween: 16,
  speed: 500,
  threshold: 10,
  watchOverflow: true,
  grid: {
    rows,
    fill: 'column',
  },
  breakpoints: {
    641: {
      slidesPerView: 2,
      spaceBetween: 16,
      grid: {
        rows,
        fill: 'column',
      },
    },
    768: {
      slidesPerView: 2,
      spaceBetween: 18,
      grid: {
        rows,
        fill: 'column',
      },
    },
    820: {
      slidesPerView: 2,
      spaceBetween: 20,
      grid: {
        rows,
        fill: 'column',
      },
    },
    951: {
      slidesPerView: 2,
      spaceBetween: 22,
      grid: {
        rows,
        fill: 'column',
      },
    },
    1101: {
      slidesPerView: 2,
      spaceBetween: 24,
      grid: {
        rows,
        fill: 'column',
      },
    },
    1281: {
      slidesPerView: 2,
      spaceBetween: 28,
      grid: {
        rows,
        fill: 'column',
      },
    },
  },
});

const initSystemSwiper = (scope = document) => {
  const wrappers = scope.querySelectorAll('.system-swiper-wr');

  wrappers.forEach((wrapper) => {
    const swiperEl = wrapper.querySelector('.swiper');

    if (!swiperEl) {
      return;
    }

    const slidesCount = swiperEl.querySelectorAll('.swiper-slide').length;

    if (!slidesCount) {
      return;
    }

    if (heightControllers.has(swiperEl)) {
      heightControllers.get(swiperEl)?.requestStableHeight();
      return;
    }

    const nextEl = wrapper.querySelector('.swiper-button-next');
    const prevEl = wrapper.querySelector('.swiper-button-prev');

    if (slidesCount <= 4) {
      swiperEl.style.setProperty('--system-static-rows', `${Math.ceil(slidesCount / 2)}`);
    } else if (slidesCount <= 5) {
      const staticRows = Math.ceil(slidesCount / 2);
      const mobileBreakpoint = window.matchMedia('(max-width: 640px)');
      const syncStaticRows = () => {
        requestAnimationFrame(() => {
          if (!mobileBreakpoint.matches) {
            swiperEl.style.setProperty('--system-static-rows', `${staticRows}`);
          }
        });
      };

      syncStaticRows();
      swiperOnResize('(max-width: 640px)', swiperEl, {
        ...createSwiperSettings(staticRows),
        navigation: nextEl && prevEl ? {
          nextEl,
          prevEl,
        } : undefined,
      });
      mobileBreakpoint.addEventListener('change', syncStaticRows);
    } else {
      const swiper = new Swiper(swiperEl, {
        ...createSwiperSettings(3),
        navigation: nextEl && prevEl ? {
          nextEl,
          prevEl,
        } : undefined,
      });

      swiper.params.grid.rows = 3;
      swiper.params.grid.fill = 'column';
      swiper.originalParams.grid.rows = 3;
      swiper.originalParams.grid.fill = 'column';
      swiper.update();
    }

    heightControllers.set(swiperEl, createHeightController(swiperEl));
  });
};

initSystemSwiper();
window.initSystemSwiper = initSystemSwiper;
